import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { useRouter, useFocusEffect, Stack, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../src/api/client';
import { useAuth } from '../src/context/AuthContext';
import { useToast } from '../src/components/Toast';
import Button from '../src/components/Button';
import InlineConfirm from '../src/components/InlineConfirm';
import Card from '../src/components/Card';
import { useTheme } from '../src/context/ThemeContext';
import type { ColorTheme } from '../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../src/utils/theme';

// Stripe Web SDK — Vite picks ../src/stripe/webElements.web.tsx (real),
// Metro picks ../src/stripe/webElements.tsx (null stubs).  Same trick
// for the native SDK.  This replaces the old `if (Platform.OS) { try {
// require(...) } catch {} }` dance which silently failed under Vite
// (no `require` in browser ES modules) and broke the Add Card flow.
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
  loadStripe,
} from '../src/stripe/webElements';
import {
  usePaymentSheet as nativeUsePaymentSheet,
  retrieveSetupIntent as nativeRetrieveSetupIntent,
} from '../src/stripe/nativeStripe';


import ContentContainer from '../src/components/ContentContainer';
import { getStripePromise } from '../src/stripe/StripeProvider';

interface PaymentMethod {
  readonly id: string;
  readonly brand: string | null;
  readonly last4: string | null;
  readonly expMonth: number | null;
  readonly expYear: number | null;
  readonly isDefault: boolean;
}

/** PaymentMethodsScreen - Manage saved cards via Stripe; supports web + native Stripe SDKs. */
export default function PaymentMethodsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { isAuthenticated, isLoading, stripePublishableKey } = useAuth();
  const stripePromise = (Platform.OS === 'web' && stripePublishableKey) ? getStripePromise(stripePublishableKey) : null;
  const params = useLocalSearchParams<{ returnTo?: string }>();

  // Auth guard — redirect unauthenticated users
  useEffect(() => {
    if (!isLoading && !isAuthenticated) router.replace('/login');
  }, [isLoading, isAuthenticated]);

  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDeleteCard, setConfirmDeleteCard] = useState<string | null>(null);

  // Native Payment Sheet hook (must be called unconditionally for Rules of Hooks)
  const paymentSheet = (Platform.OS !== 'web' && nativeUsePaymentSheet) ? nativeUsePaymentSheet() : null;

  /** GET /api/payments/methods — load saved payment methods. */
  const fetchMethods = async () => {
    try {
      const data = await api<PaymentMethod[]>('/api/payments/methods');
      setMethods(data);
    } catch (err) { console.error('fetch payment methods:', err); } finally { setLoading(false); }
  };

  useFocusEffect(useCallback(() => { fetchMethods(); }, []));

  /** Create a Stripe SetupIntent and launch native or web card entry flow. */
  const handleAddCard = async () => {
    setAdding(true);
    try {
      const res = await api<{ clientSecret: string }>('/api/payments/setup-intent', { method: 'POST', body: {} });

      // Native: use Stripe Payment Sheet (pre-built native UI)
      if (Platform.OS !== 'web' && paymentSheet) {
        const { error: initError } = await paymentSheet.initPaymentSheet({
          setupIntentClientSecret: res.clientSecret,
          merchantDisplayName: 'RinsePay',
          style: 'automatic',
          returnURL: 'rinsepay://payment-complete',
          allowsDelayedPaymentMethods: true,
          applePay: { merchantCountryCode: 'US' },
          googlePay: { merchantCountryCode: 'US', currencyCode: 'USD', testEnv: __DEV__ },
        });
        if (initError) {
          toast.show({ type: 'error', title: 'Error', message: initError.message });
          return;
        }
        const { error: presentError } = await paymentSheet.presentPaymentSheet();
        if (presentError) {
          if (presentError.code !== 'Canceled') {
            toast.show({ type: 'error', title: 'Error', message: presentError.message });
          }
          return;
        }
        // Payment Sheet succeeded — retrieve the SetupIntent to get the paymentMethodId
        // then POST it to our backend (same as web flow)
        toast.show({ type: 'info', title: 'Processing...', message: 'Card is being saved' });
        try {
          if (nativeRetrieveSetupIntent) {
            const { setupIntent } = await nativeRetrieveSetupIntent(res.clientSecret);
            if (setupIntent?.paymentMethodId) {
              await api('/api/payments/methods', {
                method: 'POST',
                body: { paymentMethodId: setupIntent.paymentMethodId, setDefault: true },
              });
            }
          }
        } catch (e) {
          // Fall through to handleSetupComplete which will try fetchMethods
          console.warn('retrieveSetupIntent fallback:', e);
        }
        handleSetupComplete();
        return;
      }

      // Web: show inline CardElement form
      setSetupClientSecret(res.clientSecret);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setAdding(false);
    }
  };

  const handleSetupComplete = async () => {
    setSetupClientSecret(null);
    toast.show({ type: 'success', title: 'Card added successfully' });
    setLoading(true);
    await fetchMethods();
    if (params.returnTo) {
      router.back();
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await api(`/api/payments/methods/${id}`, { method: 'DELETE' });
      setMethods(prev => prev.filter(m => m.id !== id));
      toast.show({ type: 'success', title: 'Card removed' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setDeleting(null); }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await api(`/api/payments/methods/${id}/default`, { method: 'PUT' });
      setMethods(prev => prev.map(m => ({ ...m, isDefault: m.id === id })));
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  // ─── Add Card Form (Stripe Elements — web only) ───
  if (setupClientSecret && Platform.OS === 'web' && Elements && stripePromise) {
    return (
      <>
        <Stack.Screen options={{
          title: 'Add Card',
          headerLeft: () => (
            <TouchableOpacity onPress={() => setSetupClientSecret(null)} hitSlop={12} style={{ padding: spacing.sm, marginLeft: -spacing.xs }}>
              <Ionicons name="arrow-back" size={28} color={colors.primary} />
            </TouchableOpacity>
          ),
        }} />
        <ScrollView style={styles.container} contentContainerStyle={styles.content}>
          <ContentContainer maxWidth={720}>
            <Card>
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: setupClientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#65a30d',
                      borderRadius: '8px',
                      colorBackground: colors.surface,
                      colorText: colors.text,
                    },
                  },
                }}
              >
                <SetupCardForm
                  onSuccess={handleSetupComplete}
                  onCancel={() => setSetupClientSecret(null)}
                  colors={colors}
                  styles={styles}
/>
              </Elements>
            </Card>
          </ContentContainer>
        </ScrollView>
      </>
    );
  }

  // ─── Card List ───
  return (
    <>
      <Stack.Screen options={{
        title: 'Payment Methods',
        headerLeft: () => (
          <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} hitSlop={12} style={{ padding: spacing.sm, marginLeft: -spacing.xs }}>
            <Ionicons name="arrow-back" size={28} color={colors.primary} />
          </TouchableOpacity>
        ),
      }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={720}>
          {params.returnTo && methods.length === 0 && !loading && (
            <Card style={styles.promptCard}>
              <Ionicons name="card-outline" size={40} color={colors.primary} />
              <Text style={styles.promptTitle}>Add a Payment Method</Text>
              <Text style={styles.promptDesc}>You need a card on file before you can start a wash.</Text>
            </Card>
          )}

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator color={colors.primary} />
            </View>
          ) : (
            <>
              {methods.map(pm => (
                <Card key={pm.id} style={styles.cardRow}>
                  <View style={styles.cardInfo}>
                    <Text style={styles.cardBrand}>
                      {(pm.brand || 'Card').toUpperCase()}
                    </Text>
                    <Text style={styles.cardNumber}>{'\u2022\u2022\u2022\u2022'} {pm.last4}</Text>
                    <Text style={styles.cardExp}>Expires {pm.expMonth}/{pm.expYear}</Text>
                  </View>
                  <View style={styles.cardActions}>
                    {pm.isDefault ? (
                      <View style={styles.defaultBadge}>
                        <Text style={styles.defaultText}>Default</Text>
                      </View>
                    ) : (
                      <TouchableOpacity onPress={() => handleSetDefault(pm.id)} hitSlop={8}>
                        <Text style={styles.setDefaultText}>Set Default</Text>
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity onPress={() => setConfirmDeleteCard(pm.id)} disabled={deleting === pm.id} hitSlop={8}>
                      <Ionicons name="trash-outline" size={20} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                  {confirmDeleteCard === pm.id && (
                    <InlineConfirm
                      message="Remove this payment method?"
                      visible={true}
                      onConfirm={async () => {
                        setConfirmDeleteCard(null);
                        await handleDelete(pm.id);
                      }}
                      onCancel={() => setConfirmDeleteCard(null)}
                      confirmLabel="Remove"
                      loading={deleting === pm.id}
                      variant="danger"
                    />
                  )}
                </Card>
              ))}

              <Button
                title="Add Card"
                onPress={handleAddCard}
                loading={adding}
                icon={<Ionicons name="add" size={20} color={colors.white} />}
                style={styles.addBtn}
              />
            </>
          )}

          <View style={styles.secureRow}>
            <Ionicons name="lock-closed" size={14} color={colors.textLight} />
            <Text style={styles.secureText}>Card info secured by Stripe — never touches our servers</Text>
          </View>
        </ContentContainer>
      </ScrollView>
    </>
  );
}

/** SetupCardForm - Web-only Stripe Elements form inside the Elements provider. */
interface SetupCardFormProps {
  readonly onSuccess: () => void;
  readonly onCancel: () => void;
  readonly colors: ColorTheme;
  readonly styles: ReturnType<typeof createStyles>;
}

function SetupCardForm({ onSuccess, onCancel, colors, styles }: SetupCardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const toast = useToast();

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setProcessing(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        toast.show({ type: 'error', title: 'Error', message: error.message });
      } else if (setupIntent?.status === 'succeeded') {
        // payment_method can be a string ID or an object with .id — Stripe SDK type varies
        const pmRaw = setupIntent.payment_method;
        const pmId = typeof pmRaw === 'string' ? pmRaw
          : typeof pmRaw === 'object' && pmRaw !== null ? (pmRaw as Record<string, unknown>).id as string
          : null;

        if (pmId) {
          // POST /api/payments/methods — attach card to customer
          await api('/api/payments/methods', {
            method: 'POST',
            body: { paymentMethodId: pmId, setDefault: true },
          });
          onSuccess();
        } else {
          // No PM ID available — wait briefly for webhook, then refresh
          const WEBHOOK_DELAY_MS = 2000;
          toast.show({ type: 'info', title: 'Processing...', message: 'Card is being saved' });
          setTimeout(() => onSuccess(), WEBHOOK_DELAY_MS);
        }
      } else {
        toast.show({ type: 'error', title: 'Error', message: `Unexpected status: ${setupIntent?.status}` });
      }
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <View style={{ padding: spacing.md }}>
      <Text style={styles.setupTitle}>Add Payment Method</Text>
      <View style={{ marginBottom: spacing.lg }}>
        <PaymentElement
          onReady={() => setReady(true)}
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'us_bank_account'],
          }}
        />
      </View>
      <Text style={{ fontSize: fontSize.xs, color: colors.textLight, textAlign: 'center', marginBottom: spacing.md, lineHeight: 16 }}>
        By saving your card, you authorize RinsePay to charge this payment method for future car wash purchases you initiate, including pre-authorized holds for self-serve washes.
      </Text>
      {!ready && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={colors.primary} />
        </View>
      )}
      <Button
        title={processing ? 'Saving...' : 'Save Card'}
        onPress={handleSubmit}
        loading={processing}
        disabled={!ready || processing}
        size="lg"
      />
      <Button title="Cancel" onPress={onCancel} variant="ghost" style={{ marginTop: spacing.sm }} />
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  loadingContainer: { alignItems: 'center', padding: spacing.xl },

  promptCard: { alignItems: 'center', padding: spacing.xl, marginBottom: spacing.md },
  promptTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md },
  promptDesc: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.sm, maxWidth: 280 },

  cardRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  cardInfo: { flex: 1 },
  cardBrand: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  cardNumber: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginTop: 2, fontFamily: 'monospace' },
  cardExp: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  cardActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  defaultBadge: { backgroundColor: c.success + '20', paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.full },
  defaultText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.success },
  setDefaultText: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium },

  addBtn: { marginTop: spacing.md },
  secureRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, marginTop: spacing.lg },
  secureText: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center' },

  setupTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.lg, textAlign: 'center' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
